import React, { useState, useEffect, useCallback } from 'react';
import { Link } from 'react-router-dom';
import { useLanguage } from '../context/LanguageContext';
import {
  Plus, Search, Pin, PinOff, Pencil, Trash2, Download, X,
  FileText, Lightbulb, CheckSquare, Users, Target,
  DollarSign, Code, Megaphone, Filter, ArrowLeft, ExternalLink, FileDown
} from 'lucide-react';

const API = process.env.REACT_APP_BACKEND_URL;
const t = (lang, fr, en) => lang === 'fr' ? fr : en;

const ICON_MAP = {
  'file-text': FileText,
  'lightbulb': Lightbulb,
  'check-square': CheckSquare,
  'users': Users,
  'target': Target,
  'dollar-sign': DollarSign,
  'code': Code,
  'megaphone': Megaphone,
};

export default function AdminNotesPage() {
  const { language } = useLanguage();
  const [notes, setNotes] = useState([]);
  const [categories, setCategories] = useState([]);
  const [loading, setLoading] = useState(true);
  const [activeCategory, setActiveCategory] = useState('');
  const [searchQuery, setSearchQuery] = useState('');
  const [modalOpen, setModalOpen] = useState(false);
  const [editingNote, setEditingNote] = useState(null);
  const [expandedNote, setExpandedNote] = useState(null);
  const [form, setForm] = useState({ title: '', content: '', category: 'general', pinned: false });
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    fetch(`${API}/api/admin/notes/categories`).then(r => r.json()).then(d => setCategories(d.categories || [])).catch(() => {});
  }, []);

  const loadNotes = useCallback(async () => {
    setLoading(true);
    const params = new URLSearchParams();
    if (activeCategory) params.set('category', activeCategory);
    if (searchQuery.trim()) params.set('search', searchQuery.trim());
    try {
      const res = await fetch(`${API}/api/admin/notes?${params.toString()}`);
      const data = await res.json();
      setNotes(data.notes || []);
    } catch (e) { console.error(e); }
    setLoading(false);
  }, [activeCategory, searchQuery]);

  useEffect(() => { loadNotes(); }, [loadNotes]);

  const openCreate = () => {
    setEditingNote(null);
    setForm({ title: '', content: '', category: 'general', pinned: false });
    setModalOpen(true);
  };

  const openEdit = (note) => {
    setEditingNote(note);
    setForm({ title: note.title, content: note.content, category: note.category, pinned: note.pinned });
    setModalOpen(true);
  };

  const handleSave = async () => {
    if (!form.title.trim()) return;
    setSaving(true);
    try {
      if (editingNote) {
        await fetch(`${API}/api/admin/notes/${editingNote.note_id}`, {
          method: 'PUT', headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify(form),
        });
      } else {
        await fetch(`${API}/api/admin/notes`, {
          method: 'POST', headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify(form),
        });
      }
      setModalOpen(false);
      loadNotes();
    } catch (e) { console.error(e); }
    setSaving(false);
  };

  const handleDelete = async (noteId) => {
    if (!window.confirm(t(language, 'Supprimer cette note ?', 'Delete this note?'))) return;
    try {
      await fetch(`${API}/api/admin/notes/${noteId}`, { method: 'DELETE' });
      loadNotes();
    } catch (e) { console.error(e); }
  };

  const togglePin = async (note) => {
    try {
      await fetch(`${API}/api/admin/notes/${note.note_id}`, {
        method: 'PUT', headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ pinned: !note.pinned }),
      });
      loadNotes();
    } catch (e) { console.error(e); }
  };

  const exportPdf = () => {
    const params = new URLSearchParams();
    if (activeCategory) params.set('category', activeCategory);
    params.set('lang', language);
    window.open(`${API}/api/admin/notes/export/pdf?${params.toString()}`, '_blank');
  };

  const getCat = (key) => categories.find(c => c.key === key) || categories[0] || {};

  return (
    <div className="min-h-screen bg-[#F8F9FB]" style={{ fontFamily: "'Satoshi', 'IBM Plex Sans', sans-serif" }}>
      {/* Top bar */}
      <div className="bg-white border-b border-gray-200 sticky top-0 z-40">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-4">
          <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
            <div className="flex items-center gap-4">
              <Link to="/admin/finances"
                className="flex items-center gap-2 px-3 py-2 rounded-xl bg-gray-100 text-sm font-medium text-[#4B5563] hover:bg-gray-200 transition-colors"
                data-testid="back-to-space-btn">
                <ArrowLeft className="w-4 h-4" />
                Admin
              </Link>
              <div>
                <h1 className="text-2xl sm:text-3xl font-bold text-[#0A0A0A] tracking-tight"
                  style={{ fontFamily: "'Cabinet Grotesk', sans-serif" }}
                  data-testid="notes-page-title">
                  {t(language, 'Mes Notes', 'My Notes')}
                </h1>
                <p className="text-sm text-[#4B5563] mt-0.5">{t(language, 'Espace personnel admin', 'Personal admin workspace')}</p>
              </div>
            </div>
            <div className="flex items-center gap-2.5 flex-wrap">
              <button onClick={exportPdf}
                className="flex items-center gap-2 px-4 py-2.5 rounded-xl border border-gray-200 bg-white text-sm font-medium text-[#4B5563] hover:bg-gray-50 transition-colors"
                data-testid="export-pdf-btn">
                <Download className="w-4 h-4" /> PDF
              </button>
              <button onClick={openCreate}
                className="flex items-center gap-2 px-5 py-2.5 rounded-xl bg-[#002FA7] text-white text-sm font-semibold hover:bg-[#00227A] transition-colors"
                data-testid="create-note-btn">
                <Plus className="w-4 h-4" /> {t(language, 'Nouvelle note', 'New Note')}
              </button>
            </div>
          </div>
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-6">
        {/* ===== RAPPORTS PDF ===== */}
        <div className="mb-8 bg-white rounded-2xl border border-gray-100 overflow-hidden" data-testid="pdf-reports-section">
          <div className="px-6 py-4 border-b border-gray-100 flex items-center justify-between">
            <div className="flex items-center gap-3">
              <div className="w-9 h-9 rounded-xl bg-red-50 flex items-center justify-center">
                <FileDown className="w-5 h-5 text-red-500" />
              </div>
              <div>
                <h2 className="font-bold text-[#0A0A0A] text-base" style={{ fontFamily: "'Cabinet Grotesk', sans-serif" }}>
                  {t(language, 'Rapports & Documents PDF', 'Reports & PDF Documents')}
                </h2>
                <p className="text-xs text-[#4B5563]">{t(language, 'Telecharger en francais ou en anglais', 'Download in French or English')}</p>
              </div>
            </div>
          </div>
          <div className="p-4 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3">
            {[
              {
                titleFr: 'Rapport Complet du Projet',
                titleEn: 'Complete Project Report',
                descFr: 'Architecture technique, fonctionnalites, endpoints API, modeles de donnees, taches restantes et suggestions',
                descEn: 'Technical architecture, features, API endpoints, data models, remaining tasks and suggestions',
                url: '/api/orbit/project-report/pdf',
                color: '#E05D3A',
                icon: '17 pages',
                testid: 'pdf-project-report',
              },
              {
                titleFr: 'Systeme de Recompenses',
                titleEn: 'Rewards System',
                descFr: 'Loyalty Pool, Cashback, Foundation Bonus, Repositionnement, Tiers VIP — 5 concepts de fidelite',
                descEn: 'Loyalty Pool, Cashback, Foundation Bonus, Repositioning, VIP Tiers — 5 loyalty concepts',
                url: '/api/orbit/rewards-concept/pdf',
                color: '#D4943A',
                icon: '12+ pages',
                testid: 'pdf-rewards',
              },
              {
                titleFr: 'Business Plan LocalSite',
                titleEn: 'LocalSite Business Plan',
                descFr: 'Triangle ecosysteme, carte virtuelle, 3 modeles de monetisation, tiers consommateur',
                descEn: 'Ecosystem triangle, virtual card, 3 monetization models, consumer tiers',
                url: '/api/orbit/localsite-concept/pdf',
                color: '#2D5A3D',
                icon: '10+ pages',
                testid: 'pdf-localsite',
              },
            ].map((doc) => (
              <div key={doc.testid} className="rounded-xl border border-gray-100 hover:shadow-md transition-all group overflow-hidden">
                <div className="h-1" style={{ backgroundColor: doc.color }} />
                <div className="p-4">
                  <h3 className="font-bold text-sm text-[#0A0A0A] mb-1">{language === 'fr' ? doc.titleFr : doc.titleEn}</h3>
                  <p className="text-[11px] text-[#4B5563] line-clamp-2 mb-3">{language === 'fr' ? doc.descFr : doc.descEn}</p>
                  <div className="flex items-center justify-between">
                    <span className="text-[10px] font-semibold text-[#9CA3AF] uppercase">{doc.icon}</span>
                    <div className="flex items-center gap-1.5">
                      <a href={`${API}${doc.url}?lang=fr`} target="_blank" rel="noopener noreferrer"
                        className="flex items-center gap-1 px-2.5 py-1.5 rounded-lg text-[11px] font-semibold transition-colors hover:bg-gray-100 text-[#4B5563]"
                        data-testid={`${doc.testid}-fr`}>
                        <FileDown className="w-3.5 h-3.5" /> FR
                      </a>
                      <a href={`${API}${doc.url}?lang=en`} target="_blank" rel="noopener noreferrer"
                        className="flex items-center gap-1 px-2.5 py-1.5 rounded-lg text-[11px] font-semibold transition-colors hover:bg-gray-100 text-[#4B5563]"
                        data-testid={`${doc.testid}-en`}>
                        <FileDown className="w-3.5 h-3.5" /> EN
                      </a>
                    </div>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>

        {/* Search + Filters */}
        <div className="flex flex-col sm:flex-row gap-3 mb-6">
          <div className="flex-1 relative">
            <Search className="absolute left-3.5 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
            <input type="text" value={searchQuery} onChange={(e) => setSearchQuery(e.target.value)}
              placeholder={t(language, 'Rechercher dans les notes...', 'Search notes...')}
              className="w-full pl-10 pr-4 py-2.5 rounded-xl bg-white border border-gray-200 text-sm placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-[#002FA7]/30 focus:border-[#002FA7]"
              data-testid="notes-search-input" />
          </div>
        </div>

        {/* Category Pills */}
        <div className="flex flex-wrap gap-2 mb-6" data-testid="category-pills">
          <button onClick={() => setActiveCategory('')}
            className={`flex items-center gap-1.5 px-4 py-2 rounded-full text-sm font-medium transition-all ${!activeCategory ? 'bg-[#0A0A0A] text-white shadow-md' : 'bg-white border border-gray-200 text-[#4B5563] hover:border-gray-300'}`}
            data-testid="filter-all">
            <Filter className="w-3.5 h-3.5" /> {t(language, 'Toutes', 'All')}
          </button>
          {categories.map((cat) => {
            const Icon = ICON_MAP[cat.icon] || FileText;
            return (
              <button key={cat.key} onClick={() => setActiveCategory(activeCategory === cat.key ? '' : cat.key)}
                className={`flex items-center gap-1.5 px-4 py-2 rounded-full text-sm font-medium transition-all ${activeCategory === cat.key ? 'text-white shadow-md' : 'bg-white border border-gray-200 text-[#4B5563] hover:border-gray-300'}`}
                style={activeCategory === cat.key ? { backgroundColor: cat.color } : {}}
                data-testid={`filter-${cat.key}`}>
                <Icon className="w-3.5 h-3.5" />
                {language === 'fr' ? cat.name_fr : cat.name_en}
              </button>
            );
          })}
        </div>

        {/* Notes Grid */}
        {loading ? (
          <div className="flex justify-center py-20">
            <div className="w-10 h-10 border-3 border-[#002FA7] border-t-transparent rounded-full animate-spin" />
          </div>
        ) : notes.length > 0 ? (
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4" data-testid="notes-grid">
            {notes.map((note) => {
              const cat = getCat(note.category);
              const Icon = ICON_MAP[cat.icon] || FileText;
              const isExpanded = expandedNote === note.note_id;
              return (
                <div key={note.note_id}
                  className="bg-white rounded-2xl border border-gray-100 overflow-hidden hover:shadow-lg transition-shadow group"
                  data-testid={`note-card-${note.note_id}`}>
                  {/* Color accent top bar */}
                  <div className="h-1.5" style={{ backgroundColor: cat.color }} />
                  <div className="p-5">
                    {/* Header */}
                    <div className="flex items-start justify-between mb-3">
                      <div className="flex items-center gap-2">
                        <div className="w-8 h-8 rounded-lg flex items-center justify-center flex-shrink-0"
                          style={{ backgroundColor: cat.color + '18' }}>
                          <Icon className="w-4 h-4" style={{ color: cat.color }} />
                        </div>
                        <span className="text-xs font-semibold uppercase tracking-wider" style={{ color: cat.color }}>
                          {language === 'fr' ? cat.name_fr : cat.name_en}
                        </span>
                      </div>
                      <div className="flex items-center gap-1 opacity-0 group-hover:opacity-100 transition-opacity">
                        <button onClick={() => togglePin(note)} className="p-1.5 rounded-lg hover:bg-gray-100" title={note.pinned ? 'Unpin' : 'Pin'}
                          data-testid={`pin-${note.note_id}`}>
                          {note.pinned ? <PinOff className="w-3.5 h-3.5 text-[#002FA7]" /> : <Pin className="w-3.5 h-3.5 text-gray-400" />}
                        </button>
                        <button onClick={() => openEdit(note)} className="p-1.5 rounded-lg hover:bg-gray-100"
                          data-testid={`edit-${note.note_id}`}>
                          <Pencil className="w-3.5 h-3.5 text-gray-400" />
                        </button>
                        <button onClick={() => handleDelete(note.note_id)} className="p-1.5 rounded-lg hover:bg-red-50"
                          data-testid={`delete-${note.note_id}`}>
                          <Trash2 className="w-3.5 h-3.5 text-red-400" />
                        </button>
                      </div>
                    </div>

                    {/* Pin badge */}
                    {note.pinned && (
                      <span className="inline-flex items-center gap-1 text-[10px] font-bold uppercase tracking-wider text-[#002FA7] bg-[#002FA7]/8 px-2 py-0.5 rounded-full mb-2">
                        <Pin className="w-3 h-3" /> {t(language, 'Epingle', 'Pinned')}
                      </span>
                    )}

                    {/* Title */}
                    <h3 className="font-bold text-[#0A0A0A] text-base leading-snug mb-2 cursor-pointer hover:text-[#002FA7] transition-colors"
                      onClick={() => setExpandedNote(isExpanded ? null : note.note_id)}
                      data-testid={`note-title-${note.note_id}`}>
                      {note.title}
                    </h3>

                    {/* Content preview / expanded */}
                    <p className={`text-sm text-[#4B5563] leading-relaxed whitespace-pre-wrap ${isExpanded ? '' : 'line-clamp-4'}`}>
                      {note.content}
                    </p>
                    {note.content && note.content.length > 200 && (
                      <button onClick={() => setExpandedNote(isExpanded ? null : note.note_id)}
                        className="text-xs font-semibold text-[#002FA7] mt-2 hover:underline">
                        {isExpanded ? t(language, 'Reduire', 'Show less') : t(language, 'Lire plus', 'Read more')}
                      </button>
                    )}

                    {/* Footer */}
                    <p className="text-[10px] text-gray-400 mt-3">
                      {note.updated_at?.slice(0, 10)}
                    </p>
                  </div>
                </div>
              );
            })}
          </div>
        ) : (
          <div className="text-center py-20 bg-white rounded-2xl border border-gray-100">
            <FileText className="w-12 h-12 text-gray-300 mx-auto mb-4" />
            <p className="text-[#4B5563] font-medium">{t(language, 'Aucune note', 'No notes yet')}</p>
            <p className="text-sm text-gray-400 mt-1">{t(language, 'Creez votre premiere note !', 'Create your first note!')}</p>
            <button onClick={openCreate} className="mt-4 px-5 py-2.5 bg-[#002FA7] text-white text-sm font-semibold rounded-xl hover:bg-[#00227A] transition-colors"
              data-testid="empty-create-btn">
              <Plus className="w-4 h-4 inline mr-1" /> {t(language, 'Nouvelle note', 'New Note')}
            </button>
          </div>
        )}
      </div>

      {/* Modal Create/Edit */}
      {modalOpen && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 backdrop-blur-sm p-4" data-testid="note-modal">
          <div className="bg-white rounded-2xl shadow-2xl w-full max-w-lg max-h-[90vh] overflow-y-auto">
            <div className="flex items-center justify-between p-5 border-b border-gray-100">
              <h2 className="font-bold text-lg text-[#0A0A0A]" style={{ fontFamily: "'Cabinet Grotesk', sans-serif" }}>
                {editingNote ? t(language, 'Modifier la note', 'Edit Note') : t(language, 'Nouvelle note', 'New Note')}
              </h2>
              <button onClick={() => setModalOpen(false)} className="p-2 rounded-lg hover:bg-gray-100" data-testid="modal-close-btn">
                <X className="w-5 h-5 text-gray-400" />
              </button>
            </div>
            <div className="p-5 space-y-4">
              {/* Title */}
              <div>
                <label className="block text-xs font-semibold uppercase tracking-wider text-[#4B5563] mb-1.5">
                  {t(language, 'Titre', 'Title')} *
                </label>
                <input type="text" value={form.title} onChange={(e) => setForm({ ...form, title: e.target.value })}
                  placeholder={t(language, 'Titre de la note...', 'Note title...')}
                  className="w-full px-4 py-2.5 rounded-xl border border-gray-200 text-sm focus:outline-none focus:ring-2 focus:ring-[#002FA7]/30 focus:border-[#002FA7]"
                  data-testid="note-title-input" />
              </div>

              {/* Category */}
              <div>
                <label className="block text-xs font-semibold uppercase tracking-wider text-[#4B5563] mb-1.5">
                  {t(language, 'Categorie', 'Category')}
                </label>
                <div className="grid grid-cols-4 gap-2" data-testid="note-category-grid">
                  {categories.map((cat) => {
                    const Icon = ICON_MAP[cat.icon] || FileText;
                    return (
                      <button key={cat.key} type="button"
                        onClick={() => setForm({ ...form, category: cat.key })}
                        className={`flex flex-col items-center gap-1 p-3 rounded-xl border text-xs font-medium transition-all ${form.category === cat.key ? 'border-2 shadow-md' : 'border-gray-200 hover:border-gray-300'}`}
                        style={form.category === cat.key ? { borderColor: cat.color, backgroundColor: cat.color + '10' } : {}}
                        data-testid={`cat-select-${cat.key}`}>
                        <Icon className="w-5 h-5" style={{ color: cat.color }} />
                        <span className="truncate w-full text-center" style={form.category === cat.key ? { color: cat.color } : { color: '#4B5563' }}>
                          {language === 'fr' ? cat.name_fr : cat.name_en}
                        </span>
                      </button>
                    );
                  })}
                </div>
              </div>

              {/* Content */}
              <div>
                <label className="block text-xs font-semibold uppercase tracking-wider text-[#4B5563] mb-1.5">
                  {t(language, 'Contenu', 'Content')}
                </label>
                <textarea value={form.content} onChange={(e) => setForm({ ...form, content: e.target.value })}
                  rows={8}
                  placeholder={t(language, 'Ecrivez votre note ici...', 'Write your note here...')}
                  className="w-full px-4 py-3 rounded-xl border border-gray-200 text-sm leading-relaxed resize-y focus:outline-none focus:ring-2 focus:ring-[#002FA7]/30 focus:border-[#002FA7]"
                  data-testid="note-content-input" />
              </div>

              {/* Pinned toggle */}
              <label className="flex items-center gap-3 cursor-pointer">
                <div className={`w-10 h-6 rounded-full relative transition-colors ${form.pinned ? 'bg-[#002FA7]' : 'bg-gray-200'}`}
                  onClick={() => setForm({ ...form, pinned: !form.pinned })}
                  data-testid="pin-toggle">
                  <div className={`w-4 h-4 rounded-full bg-white shadow-sm absolute top-1 transition-transform ${form.pinned ? 'translate-x-5' : 'translate-x-1'}`} />
                </div>
                <span className="text-sm text-[#4B5563] font-medium">
                  <Pin className="w-3.5 h-3.5 inline mr-1" />
                  {t(language, 'Epingler en haut', 'Pin to top')}
                </span>
              </label>
            </div>

            {/* Actions */}
            <div className="flex items-center justify-end gap-3 px-5 py-4 border-t border-gray-100">
              <button onClick={() => setModalOpen(false)}
                className="px-5 py-2.5 rounded-xl text-sm font-medium text-[#4B5563] hover:bg-gray-100 transition-colors"
                data-testid="modal-cancel-btn">
                {t(language, 'Annuler', 'Cancel')}
              </button>
              <button onClick={handleSave} disabled={saving || !form.title.trim()}
                className="px-6 py-2.5 rounded-xl bg-[#002FA7] text-white text-sm font-semibold hover:bg-[#00227A] transition-colors disabled:opacity-50"
                data-testid="modal-save-btn">
                {saving ? '...' : editingNote ? t(language, 'Sauvegarder', 'Save') : t(language, 'Creer', 'Create')}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
